import * as THREE from 'three';
import { ArenaThemeManager, ArenaThemeDefinition } from './ArenaThemeManager';

export class Arena {
  private scene: THREE.Scene;
  private floor!: THREE.Mesh;
  private torches: THREE.PointLight[] = [];
  private flameParticles: THREE.Points[] = [];
  private rings: THREE.Mesh[] = [];
  private lights: THREE.Light[] = [];
  private skybox!: THREE.Mesh;
  private fog!: THREE.FogExp2;
  private ambientParticles!: THREE.Points;

  // InstancedMesh objects (replace individual mesh arrays)
  private runeInstances!: THREE.InstancedMesh;
  private runeMaterial!: THREE.MeshBasicMaterial;
  private pillarBaseInstances!: THREE.InstancedMesh;
  private pillarShaftInstances!: THREE.InstancedMesh;
  private pillarCapitalInstances!: THREE.InstancedMesh;
  private pillarMaterial!: THREE.MeshStandardMaterial;
  private torchHolderInstances!: THREE.InstancedMesh;
  private torchHolderMaterial!: THREE.MeshStandardMaterial;

  private static readonly PILLAR_COUNT = 8;
  private static readonly RUNE_COUNT = 12;
  private static readonly TORCH_COUNT = 4;

  private themeManager: ArenaThemeManager;
  private activeTheme: ArenaThemeDefinition;

  constructor(scene: THREE.Scene, themeManager?: ArenaThemeManager) {
    this.scene = scene;
    this.themeManager = themeManager ?? new ArenaThemeManager();
    this.activeTheme = this.themeManager.getTheme();
    this.createEnvironment();
    this.applyCurrentTheme();
  }

  getThemeManager(): ArenaThemeManager {
    return this.themeManager;
  }

  /**
   * Apply the currently selected theme to all arena visuals.
   * Call this after changing the theme via the theme manager.
   */
  applyCurrentTheme(): void {
    this.activeTheme = this.themeManager.getTheme();
    this.themeManager.applyTheme(
      this.floor,
      this.pillarMaterial,
      this.torches,
      this.torchHolderMaterial,
      this.flameParticles,
      this.rings,
      this.runeMaterial,
      this.lights,
      this.skybox,
      this.fog,
      this.ambientParticles
    );
  }

  private createEnvironment(): void {
    this.createFog();
    this.createFloor();
    this.createPillars();
    this.createTorches();
    this.createAmbientParticles();
    this.createSkybox();
    this.createLighting();
  }

  private createFog(): void {
    // Lighter fog for better visibility
    this.fog = new THREE.FogExp2(0x2a2a3e, 0.015);
    this.scene.fog = this.fog;
  }

  private createFloor(): void {
    const geometry = new THREE.CircleGeometry(20, 64);
    const material = new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,
      roughness: 0.8,
      metalness: 0.2
    });
    this.floor = new THREE.Mesh(geometry, material);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.scene.add(this.floor);

    const innerRingGeometry = new THREE.RingGeometry(3, 3.3, 64);
    const runesMaterial = new THREE.MeshBasicMaterial({
      color: 0xC9A227,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide
    });
    const innerRing = new THREE.Mesh(innerRingGeometry, runesMaterial);
    innerRing.rotation.x = -Math.PI / 2;
    innerRing.position.y = 0.01;
    this.scene.add(innerRing);
    this.rings.push(innerRing);

    const outerRingGeometry = new THREE.RingGeometry(8, 8.3, 64);
    const outerRing = new THREE.Mesh(outerRingGeometry, runesMaterial.clone());
    outerRing.rotation.x = -Math.PI / 2;
    outerRing.position.y = 0.01;
    this.scene.add(outerRing);
    this.rings.push(outerRing);

    this.createRuneSymbols();
  }

  private createRuneSymbols(): void {
    const count = Arena.RUNE_COUNT;
    const radius = 5.5;

    const runeGeometry = new THREE.PlaneGeometry(0.8, 0.8);
    this.runeMaterial = new THREE.MeshBasicMaterial({
      color: 0xC9A227,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });

    this.runeInstances = new THREE.InstancedMesh(runeGeometry, this.runeMaterial, count);

    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      quaternion.setFromEuler(new THREE.Euler(-Math.PI / 2, 0, angle));
      matrix.compose(new THREE.Vector3(x, 0.02, z), quaternion, scale);
      this.runeInstances.setMatrixAt(i, matrix);
    }

    this.runeInstances.instanceMatrix.needsUpdate = true;
    this.scene.add(this.runeInstances);
  }

  private createPillars(): void {
    const count = Arena.PILLAR_COUNT;
    const radius = 15;

    // Shared stone material for all pillar components
    this.pillarMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a4a4a,
      roughness: 0.9,
      metalness: 0.1
    });

    // Create geometries for each pillar component
    const baseGeometry = new THREE.CylinderGeometry(0.8, 1, 0.5, 8);
    const shaftGeometry = new THREE.CylinderGeometry(0.6, 0.7, 6, 8);
    const capitalGeometry = new THREE.CylinderGeometry(0.9, 0.6, 0.6, 8);

    this.pillarBaseInstances = new THREE.InstancedMesh(baseGeometry, this.pillarMaterial, count);
    this.pillarShaftInstances = new THREE.InstancedMesh(shaftGeometry, this.pillarMaterial, count);
    this.pillarCapitalInstances = new THREE.InstancedMesh(capitalGeometry, this.pillarMaterial, count);

    this.pillarBaseInstances.castShadow = true;
    this.pillarShaftInstances.castShadow = true;
    this.pillarCapitalInstances.castShadow = true;

    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      // Base at y = 0.25
      position.set(x, 0.25, z);
      matrix.compose(position, quaternion, scale);
      this.pillarBaseInstances.setMatrixAt(i, matrix);

      // Shaft at y = 3.5
      position.set(x, 3.5, z);
      matrix.compose(position, quaternion, scale);
      this.pillarShaftInstances.setMatrixAt(i, matrix);

      // Capital at y = 6.8
      position.set(x, 6.8, z);
      matrix.compose(position, quaternion, scale);
      this.pillarCapitalInstances.setMatrixAt(i, matrix);
    }

    this.pillarBaseInstances.instanceMatrix.needsUpdate = true;
    this.pillarShaftInstances.instanceMatrix.needsUpdate = true;
    this.pillarCapitalInstances.instanceMatrix.needsUpdate = true;

    this.scene.add(this.pillarBaseInstances);
    this.scene.add(this.pillarShaftInstances);
    this.scene.add(this.pillarCapitalInstances);
  }

  private createTorches(): void {
    const torchPositions = [
      { x: -8, z: 0 },
      { x: 8, z: 0 },
      { x: 0, z: -8 },
      { x: 0, z: 8 }
    ];

    // Create instanced torch holders
    const holderGeometry = new THREE.CylinderGeometry(0.1, 0.15, 1, 6);
    this.torchHolderMaterial = new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,
      roughness: 0.8,
      metalness: 0.3
    });
    this.torchHolderInstances = new THREE.InstancedMesh(
      holderGeometry, this.torchHolderMaterial, Arena.TORCH_COUNT
    );

    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);

    torchPositions.forEach((pos, i) => {
      const torch = new THREE.PointLight(0xFF6B35, 2, 20);
      torch.position.set(pos.x, 4, pos.z);
      torch.castShadow = true;
      this.scene.add(torch);
      this.torches.push(torch);

      // Set instance matrix for torch holder
      position.set(pos.x, 3.5, pos.z);
      matrix.compose(position, quaternion, scale);
      this.torchHolderInstances.setMatrixAt(i, matrix);

      this.createFlameParticles(pos.x, 4, pos.z);
    });

    this.torchHolderInstances.instanceMatrix.needsUpdate = true;
    this.scene.add(this.torchHolderInstances);
  }

  private createFlameParticles(x: number, y: number, z: number): void {
    const particleCount = 20;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 0.3;
      positions[i * 3 + 1] = Math.random() * 0.5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.3;

      colors[i * 3] = 1;
      colors[i * 3 + 1] = 0.3 + Math.random() * 0.4;
      colors[i * 3 + 2] = 0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });

    const flame = new THREE.Points(geometry, material);
    flame.position.set(x, y, z);
    this.scene.add(flame);
    this.flameParticles.push(flame);
  }

  private createAmbientParticles(): void {
    const particleCount = 200;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 40;
      positions[i * 3 + 1] = Math.random() * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 40;

      colors[i * 3] = 0.8 + Math.random() * 0.2;
      colors[i * 3 + 1] = 0.6 + Math.random() * 0.2;
      colors[i * 3 + 2] = 0.1 + Math.random() * 0.2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });

    this.ambientParticles = new THREE.Points(geometry, material);
    this.scene.add(this.ambientParticles);
  }

  private createSkybox(): void {
    const geometry = new THREE.SphereGeometry(50, 32, 32);
    const material = new THREE.MeshBasicMaterial({
      color: 0x0a0a15,
      side: THREE.BackSide
    });
    this.skybox = new THREE.Mesh(geometry, material);
    this.scene.add(this.skybox);
  }

  private createLighting(): void {
    // Brighter ambient light for better visibility
    const ambientLight = new THREE.AmbientLight(0x6666aa, 0.8);
    this.scene.add(ambientLight);
    this.lights.push(ambientLight);

    // Stronger main directional light
    const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
    mainLight.position.set(5, 15, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 50;
    this.scene.add(mainLight);
    this.lights.push(mainLight);

    // Fill light from opposite side
    const fillLight = new THREE.DirectionalLight(0x8899cc, 0.6);
    fillLight.position.set(-5, 8, -5);
    this.scene.add(fillLight);
    this.lights.push(fillLight);

    // Add rim light from behind for depth
    const rimLight = new THREE.DirectionalLight(0xffffee, 0.4);
    rimLight.position.set(0, 5, -15);
    this.scene.add(rimLight);
    this.lights.push(rimLight);

    // Hemisphere light for natural ambient
    const hemiLight = new THREE.HemisphereLight(0x8888ff, 0x444422, 0.5);
    this.scene.add(hemiLight);
    this.lights.push(hemiLight);
  }

  update(_delta: number): void {
    const now = Date.now();
    const t = now * 0.001; // seconds for smoother sine inputs
    const theme = this.activeTheme;

    // Torch flicker with more organic variation, using theme base intensity
    const torchBase = theme.torchIntensity * 0.8;
    this.torches.forEach((torch, index) => {
      const flicker = torchBase
        + Math.sin(now * 0.013 + index * 2.1) * 0.3
        + Math.sin(now * 0.031 + index * 0.7) * 0.15
        + Math.sin(now * 0.071 + index * 5.3) * 0.1;
      torch.intensity = flicker;
    });

    // Flame particle animation -- bob upward and reset, speed from theme
    const flameSpeed = theme.flameRiseSpeed;
    this.flameParticles.forEach((flame) => {
      const positions = flame.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length / 3; i++) {
        // Rise and flicker
        positions[i * 3 + 1] += (0.01 + Math.sin(t * 5 + i * 1.7) * 0.005) * flameSpeed;
        positions[i * 3] += Math.sin(t * 3 + i * 2.3) * 0.002;
        // Reset particles that rise too high
        if (positions[i * 3 + 1] > 0.8) {
          positions[i * 3 + 1] = Math.random() * 0.1;
          positions[i * 3] = (Math.random() - 0.5) * 0.3;
          positions[i * 3 + 2] = (Math.random() - 0.5) * 0.3;
        }
      }
      flame.geometry.attributes.position.needsUpdate = true;
    });

    // Rune glow pulse -- collective pulse on shared material
    const runeSpeed = theme.runeGlowSpeed;
    const runeRange = theme.runeGlowRange;
    const runeBase = theme.runeBaseOpacity;
    const wave = runeBase + Math.sin(t * runeSpeed) * runeRange;
    this.runeMaterial.opacity = wave;

    // Ring glow pulse
    this.rings.forEach((ring, index) => {
      const mat = ring.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.2 + Math.sin(t * 0.8 + index * Math.PI) * 0.15;
    });

    // Ambient particles drift, speed from theme
    const driftSpeed = theme.ambientDriftSpeed;
    if (this.ambientParticles) {
      const positions = this.ambientParticles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length / 3; i++) {
        positions[i * 3 + 1] += Math.sin(t + i) * 0.002 * driftSpeed;
        positions[i * 3] += Math.sin(t * 0.5 + i * 0.5) * 0.001 * driftSpeed;

        if (positions[i * 3 + 1] > 10) {
          positions[i * 3 + 1] = 0;
        }
      }
      this.ambientParticles.geometry.attributes.position.needsUpdate = true;
    }
  }

  getPlayerPosition(): THREE.Vector3 {
    return new THREE.Vector3(0, 1, 8);
  }

  getEnemyPosition(): THREE.Vector3 {
    return new THREE.Vector3(0, 1, -8);
  }

  dispose(): void {
    // Helper to dispose mesh geometry and materials
    const disposeMesh = (mesh: THREE.Mesh | THREE.Points | THREE.InstancedMesh) => {
      mesh.geometry?.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(mat => mat.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    };

    // Floor
    if (this.floor) {
      this.scene.remove(this.floor);
      disposeMesh(this.floor);
    }

    // Rings
    this.rings.forEach(ring => {
      this.scene.remove(ring);
      disposeMesh(ring);
    });
    this.rings = [];

    // Runes (InstancedMesh)
    if (this.runeInstances) {
      this.scene.remove(this.runeInstances);
      disposeMesh(this.runeInstances);
    }

    // Pillars (InstancedMesh x3)
    if (this.pillarBaseInstances) {
      this.scene.remove(this.pillarBaseInstances);
      this.pillarBaseInstances.geometry?.dispose();
    }
    if (this.pillarShaftInstances) {
      this.scene.remove(this.pillarShaftInstances);
      this.pillarShaftInstances.geometry?.dispose();
    }
    if (this.pillarCapitalInstances) {
      this.scene.remove(this.pillarCapitalInstances);
      this.pillarCapitalInstances.geometry?.dispose();
    }
    // Dispose shared pillar material once
    this.pillarMaterial?.dispose();

    // Torches
    this.torches.forEach(torch => this.scene.remove(torch));
    this.torches = [];

    // Torch holders (InstancedMesh)
    if (this.torchHolderInstances) {
      this.scene.remove(this.torchHolderInstances);
      disposeMesh(this.torchHolderInstances);
    }

    // Flame particles
    this.flameParticles.forEach(flame => {
      this.scene.remove(flame);
      disposeMesh(flame);
    });
    this.flameParticles = [];

    // Ambient particles
    if (this.ambientParticles) {
      this.scene.remove(this.ambientParticles);
      disposeMesh(this.ambientParticles);
    }

    // Skybox
    if (this.skybox) {
      this.scene.remove(this.skybox);
      disposeMesh(this.skybox);
    }

    // Lights
    this.lights.forEach(light => this.scene.remove(light));
    this.lights = [];

    // Clear fog
    this.scene.fog = null;

    // Dispose theme manager
    this.themeManager.dispose();
  }
}
